import type { EventDateRange } from './types';
import { isValidDate } from './validate';

/** Chronological order by start, then end; keep unfinished entries at the bottom. */
export function sortEventDates(dates: EventDateRange[]): EventDateRange[] {
  const compare = (a: string, b: string) => a < b ? -1 : a > b ? 1 : 0;
  return [...dates].sort((a, b) => {
    if (!a.start || !b.start) return a.start ? -1 : b.start ? 1 : 0;
    return compare(a.start, b.start) || compare(a.end || a.start, b.end || b.start);
  });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Compact, locale-independent label for an event's dates, e.g. "Jun 12, 2026", "Jun 12–14, 2026",
 * "Jun 30 – Jul 2, 2026", or "Jun 12, Jul 3–5, 2026". The year is written once when every date shares it.
 */
export function formatEventDatesLabel(dates: EventDateRange[]): string {
  const valid = sortEventDates(dates).filter((r) => isValidDate(r.start) && (!r.end || isValidDate(r.end)));
  if (!valid.length) return '';
  const parts = valid.map((r) => {
    const end = r.end && r.end > r.start ? r.end : r.start;
    return { s: r.start.split('-').map(Number), e: end.split('-').map(Number) };
  });
  const oneYear = parts.every((p) => p.s[0] === parts[0].s[0] && p.e[0] === parts[0].s[0]);
  const md = ([, m, d]: number[]) => `${MONTHS[m - 1]} ${d}`;
  const label = parts.map(({ s, e }) => {
    const withYear = (ymd: number[]) => (oneYear ? md(ymd) : `${md(ymd)}, ${ymd[0]}`);
    if (s.join() === e.join()) return withYear(s);
    if (s[0] === e[0] && s[1] === e[1]) return oneYear ? `${md(s)}–${e[2]}` : `${md(s)}–${e[2]}, ${s[0]}`;
    if (s[0] === e[0] && !oneYear) return `${md(s)} – ${md(e)}, ${s[0]}`;
    return `${withYear(s)} – ${withYear(e)}`;
  }).join(', ');
  return oneYear ? `${label}, ${parts[0].s[0]}` : label;
}

/** Today's date on this device, YYYY-MM-DD: the day of signing while a registration is being signed. */
export function localTodayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
